<!-- Shared body inner content (used by both dialog and inline modes) -->
<ng-template #fileSelectionBodyInner>
    <div class="file-selection-dialog flex flex-col gap-1 px-3 lg:px-4 py-2 h-full overflow-hidden">
        @if(bodyHeader()) {
        <div class="mb-3 border-b border-gray-200 pb-3">
            <ng-container *ngTemplateOutlet="bodyHeader()!" />
        </div>
        }
        @if(config().tabs && config().tabs!.length > 0) {
        <div class="mb-4">
            <p-tabs [value]="selectedTabIndex().toString()" (valueChange)="handleTabChange($event)"
                class="custom-tabview w-full">
                <p-tablist>
                    <p-tab *ngFor="let tab of config().tabs; let index = index" [value]="index.toString()"
                        [disabled]="tab.disabled" [pTooltip]="tab.tooltip || ''" [tooltipPosition]="'top'"
                        [showDelay]="300" class="flex items-center !gap-2 px-2 py-1">
                        <div class="flex items-center gap-2">
                            @if(tab.icon) {
                            <i [class]="tab.icon"></i>
                            }
                            <span class="ml-1 text-gray-500 font-sans text-sm whitespace-nowrap">{{
                                tab.label
                                }}</span>
                            @if(tab.badge) {
                            <span class="ml-1 px-2 py-0.5 text-xs bg-orange-100 text-orange-600 rounded-full">
                                {{ tab.badge }}
                            </span>
                            }
                        </div>
                    </p-tab>
                </p-tablist>
            </p-tabs>
        </div>
        }
        <div class="flex flex-col lg:grid lg:grid-cols-2 gap-4 flex-1 min-h-0 overflow-hidden">
            <!-- Left Panel -->
            @if(selectedTab()?.type === TabType.UPLOAD) {
            <div
                class="flex-1 flex flex-col min-h-0 border-b lg:border-b-0 lg:border-r border-gray-200 pb-4 lg:pb-0 lg:pr-4">
                <div class="w-[100%] mt-4 file-upload-wrapper">
                    <app-file-drop class="w-full" [multiple]="true" [fileTypes]="allowedFileTypes()"
                        [maxTotalFileSize]="config().maxTotalUploadedFileSize" [maxFileSize]="localUploadMaxFileSize()"
                        [maxFiles]="uploadMaxFiles()" [existingFiles]="uploadedFiles()"
                        (uploadData)="onFileUpload($event)" (removeFile)="onFileRemove($event)">
                    </app-file-drop>
                </div>
            </div>
            }@else{
            <div
                class="flex-1 flex flex-col min-h-0 border-b lg:border-b-0 lg:border-r border-gray-200 pb-4 lg:pb-0 lg:pr-4">
                <!-- Dropdown -->
                <div class="flex flex-row items-center justify-between gap-4">
                    @if(dropdownSelectOptions().length > 0) {
                    <div class="mb-4" [class.flex-1]="showAfterDropdownTemplate()"
                        [class.min-w-0]="showAfterDropdownTemplate()" [class.w-full]="!showAfterDropdownTemplate()">
                        <app-select [(value)]="selectedDropdown" [options]="dropdownSelectOptions()" [config]="{
                                        placeholder: selectedTab() ? 'Select ' + selectedTab()!.label : 'Select option',
                                        searchable: true,
                                        clearable: false
                                    }" (onChange)="handleDropdownChange($event)" />
                    </div>
                    }

                    @if(showAfterDropdownTemplate()) {
                    <div class="mb-4">
                        <ng-container *ngTemplateOutlet="afterDropdownTemplate()!" />
                    </div>
                    }
                </div>

                @if(supportSubFolders() && (currentFolderId() || folderBreadcrumb().length > 0)) {
                <div class="mb-3 flex flex-wrap items-center gap-1 text-sm">
                    <app-button variant="text" class="flex items-center gap-1 text-gray-600" size="sm"
                        (clicked)="navigateBack()" [icon]="'chevron-left'" [iconSource]="'lucide'" [iconSize]="'lg'">
                    </app-button>
                    @if(folderBreadcrumb().length > 0) {
                    <!-- <span class="text-gray-400">/</span> -->
                    @for(crumb of folderBreadcrumb(); track crumb.id) {
                    <span class="text-gray-600">{{ crumb.name }}</span>
                    @if(!$last) {
                    <span class="text-gray-400">/</span>
                    }
                    }
                    }
                </div>
                }

                <!-- Search Bar -->
                @if(this.config().showSearch !== false && shouldShowItems()) {
                <div class="mb-4">
                    <app-search-bar [placeholder]="getSearchPlaceholder()" [width]="'100%'"
                        (searchChange)="handleSearchInput($event)" />
                </div>
                }

                @if(loading()) {
                <!-- Select All Skeleton -->
                <div class="mb-3 ml-3">
                    <div class="flex flex-row items-center gap-2">
                        <p-skeleton shape="circle" width="1.25rem" height="1.25rem" />
                        <p-skeleton width="8rem" height="1rem" />
                    </div>
                </div>

                <!-- File List Skeleton -->
                <div class="flex-1 overflow-y-auto file-list">
                    @for(item of [1,2,3,4,5]; track item) {
                    <div class="ml-3 py-4 pr-4 border-b">
                        <div class="flex flex-row items-center gap-3 py-2">
                            <p-skeleton shape="circle" width="1.25rem" height="1.25rem" />
                            <p-skeleton shape="circle" width="2rem" height="2.5rem" />
                            <div class="flex flex-1 flex-col min-w-0 gap-2">
                                <p-skeleton width="14rem" height="1rem" />
                                <p-skeleton width="6rem" height="0.875rem" />
                            </div>
                        </div>
                    </div>
                    }
                </div>

                <!-- Paginator Skeleton -->
                <div class="mt-2 pt-2 border-t border-gray-200">
                    <p-skeleton width="100%" height="2.5rem" />
                </div>
                } @else {
                <!-- Select All -->
                @if(shouldShowItems() && items().length > 0) {
                @if(hasSelectableItems()) {
                <div class="mb-3 ml-3">
                    <app-checkbox [value]="selectAllChecked()" (onChange)="toggleSelectAll($event)" [config]="{
                                            binary: true,
                                            label: getSelectAllLabel(),
                                            style: {'padding': '0px 0px'},
                                            disabled: isSubmitting()
                                        }" />
                </div>
                }
                }

                <!-- File List -->
                <div class="flex-1 overflow-y-auto file-list">
                    @if(shouldShowItems()) {
                    @if(items().length > 0) {
                    @for(file of items(); track file.id) {
                    <div class="ml-3 py-4 pr-4 border-b cursor-pointer">
                        @if(supportSubFolders() && file.isFolder) {
                        <div class="flex w-full flex-row items-center gap-3 rounded "
                            (click)="navigateToFolder(file.id, file.name)">
                            <div class="flex h-6 w-6 flex-shrink-0 items-center justify-center">
                                <app-icon [type]="file['folderIcon'] || getFolderIconType()"
                                    [source]="file['folderIconSource'] || getFolderIconSource()"
                                    [size]="'lg'"></app-icon>
                            </div>
                            <div class="flex flex-1 flex-row items-center gap-2 min-w-0">
                                <div class="flex flex-col items-start">
                                    <span class="font-semibold text-xs text-gray-900 truncate">{{ file.name
                                        }}</span>
                                </div>
                            </div>
                            <i class="pi pi-chevron-right text-gray-400"></i>
                        </div>
                        } @else if(fileItemTemplate()) {
                        <ng-container
                            *ngTemplateOutlet="fileItemTemplate()!; context: { $implicit: file, selected: isFileSelected(file), toggleSelection: getToggleSelectionFn(file) }" />
                        } @else {
                        <div class="flex flex-row items-center gap-3">
                            <app-checkbox [value]="isFileSelected(file)" (onChange)="toggleFileSelection(file, $event)"
                                [config]="{ binary: true, disabled: isSubmitting() }" />

                            <div class="flex flex-1 flex-row items-center gap-2 min-w-0">
                                <app-file-icon [type]="file.metadata?.fileType ?? getFileType(file.name)" size="xxl" />
                                <div class="flex flex-col items-start">
                                    <span class="font-semibold text-xs text-gray-900 truncate">{{ file.name
                                        }}</span>
                                    @if(file.size) {
                                    <div class="text-xs text-gray-500">{{ formatFileSize(file.size) }}</div>
                                    }
                                </div>
                            </div>
                        </div>
                        }
                    </div>
                    }
                    } @else {
                    <!-- Empty state -->
                    @if(emptyStateTemplate()) {
                    <ng-container
                        *ngTemplateOutlet="emptyStateTemplate()!; context: { tab: selectedTab(), emptyMessage: config().emptyMessage || 'No results found', selectedDropdown: selectedDropdown() }" />
                    } @else {
                    <div class="flex flex-col items-center justify-center h-full text-gray-500">
                        <img [src]="'images/bob_agent/404-bob.svg' | assetUrl" class="w-[80%]" />
                        <p>{{ config().emptyMessage || 'No results found' }}</p>
                    </div>
                    }
                    }
                    } @else {
                    <!-- No dropdown selected (only show when tabs exist and dropdown options are provided) -->
                    @if(config().tabs && config().tabs!.length > 0 && dropdownSelectOptions().length > 0) {
                    <div class="flex flex-col items-center justify-center h-full text-gray-500">
                        <i class="pi pi-arrow-up text-4xl mb-3"></i>
                        <p>Select a {{ selectedTab()?.label || 'option' }} to view files</p>
                    </div>
                    }
                    }
                </div>

                <!-- Paginator -->
                @if(shouldShowItems() && totalRecords() > 0) {
                <div class="mt-2 pt-2 border-t border-gray-200" #paginatorContainer>
                    <p-paginator [pageLinkSize]="pageLinkSize()" [first]="currentPage() * pageSize()"
                        [rows]="pageSize()" appendTo="body" [totalRecords]="totalRecords()"
                        [rowsPerPageOptions]="rowsPerPageOptions()" (onPageChange)="handlePageChange($event)" />
                </div>
                }
                }
            </div>
            }

            <!-- Right Panel - Always visible -->
            <div class="flex-1 flex flex-col min-h-0">
                <h2 class="font-bold text-base lg:text-lg mb-3">
                    {{ config().selectedPanelTitle || 'Selected files' }}
                    @if(allSelectedFiles().length > 0) {
                    <span class="text-sm font-normal text-gray-500 ml-1">({{ allSelectedFiles().length }})</span>
                    }
                </h2>

                <div class="flex-1 overflow-y-auto flex flex-col gap-3 selected-files-list">
                    <!-- Show all selected files (including uploaded) -->
                    @if(selectedFileGroupsByTab().length > 0) {
                    @for(tabGroup of selectedFileGroupsByTab(); track tabGroup.tabId) {
                    <div class="flex flex-col gap-2">
                        <!-- Tab Header (only shown when tabs are configured) -->
                        @if(config().tabs && config().tabs!.length > 0) {
                        <span class="flex items-center gap-2">
                            <h3 class="font-semibold text-gray-700">{{ tabGroup.tabLabel }}</h3>
                            <h3 class="text-sm text-gray-500">({{ tabGroup.count }})</h3>
                        </span>
                        }

                        <!-- Sub-groups within the tab -->
                        @for(group of tabGroup.subGroups; track group.tabId + '_' + group.dropdownId +
                        (group.groupKey || '')) {
                        <div class="flex flex-col gap-1" [class.ml-3]="config().tabs && config().tabs!.length > 0">
                            <!-- Group Header (dropdown or project name, only shown when tabs are configured) -->
                            @if((config().tabs && config().tabs!.length > 0) && (group.groupLabel ||
                            (group.dropdownLabel && group.dropdownLabel.trim() !== ''))) {
                            <div class="flex flex-col gap-0 p-0">
                                @if(group.groupLabel) {
                                <p class="text-sm text-gray-600">{{ group.groupLabel }}</p>
                                } @else if(group.dropdownLabel && group.dropdownLabel.trim() !== '') {
                                <p class="text-sm text-gray-600">{{ group.dropdownLabel }}</p>
                                }
                            </div>
                            }

                            <!-- Group Files -->
                            <div class="flex flex-col gap-3">
                                @for(file of group.files; track file.id) {
                                <div class="">
                                    @if(selectedFileTemplate()) {
                                    <!-- Custom template -->
                                    <ng-container
                                        *ngTemplateOutlet="selectedFileTemplate()!; context: { $implicit: file, selected: true, toggleSelection: getSelectedToggleSelectionFn(file) }" />
                                    } @else {
                                    <!-- Default template -->
                                    <div class="flex items-center gap-3">
                                        <div class="flex flex-1 flex-row items-center gap-2 min-w-0">
                                            <app-file-icon [type]="file.metadata?.fileType ?? getFileType(file.name)"
                                                size="xxl" [imageUrl]="getImageUrl(file)" />
                                            <div class="flex flex-col items-start max-w-[90%]">
                                                <span
                                                    class="font-semibold text-xs text-gray-900 truncate max-w-[100%]">{{
                                                    file.name }}</span>
                                                @if(file.size) {
                                                <div class="text-xs text-gray-500">{{ formatFileSize(file.size) }}
                                                </div>
                                                }
                                            </div>
                                        </div>

                                        <app-button variant="outlined" severity="danger" icon="pi pi-trash" size="sm"
                                            [disabled]="isSubmitting()" (clicked)="removeSelectedFile(file)" />
                                    </div>
                                    }
                                </div>
                                <hr>
                                }
                            </div>
                        </div>
                        }
                    </div>
                    }
                    } @else {
                    <!-- Empty state -->
                    @if(selectedEmptyTemplate()) {
                    <ng-container *ngTemplateOutlet="selectedEmptyTemplate()!" />
                    } @else {
                    <div class="empty-state">
                        <img [src]="'images/bob_agent/left-point-bob.svg' | assetUrl" />
                        <p>{{ config().selectedEmptyMessage || 'Select or upload files to add them' }}</p>
                    </div>
                    }
                    }
                </div>
            </div>
        </div>
    </div>
</ng-template>

<!-- Shared footer template -->
<ng-template #fileSelectionFooter>
    <div class="flex w-full items-center px-4 py-3 border-t border-gray-200"
        [ngClass]="config().showTotalAttachmentsCount ? 'justify-between' : 'justify-end'">
        @if(config().showTotalAttachmentsCount) {
        <span class="text-sm text-gray-500">{{allSelectedFiles().length}}/{{config().maxTotalUploadedAttachments}}
            files selected</span>
        }
        <div class="flex gap-2">
            <!-- <app-button variant="outlined" [label]="getCancelLabel()" [disabled]="isSubmitting()"
                (clicked)="handleCancel()" /> -->
            <app-button variant="filled" severity="primary" [label]="getSubmitLabel()"
                [disabled]="disableSubmitButton()" [loading]="isSubmitting()" (clicked)="handleSubmit()" />
        </div>
    </div>
</ng-template>

@if(mode() === FileSelectionMode.INLINE) {
<div class="file-selection-inline flex flex-col gap-1 h-full overflow-hidden">
    <ng-container *ngTemplateOutlet="fileSelectionBodyInner" />
</div>
} @else {
<app-dialog [(visible)]="visible" [fullscreenOnMobile]="true" [showCloseButton]="false"
    [width]="config().dialogWidth || '60vw'" class="!p-0 gap-0 h-[90vh] max-w-[65rem]" (closed)="handleDialogClose()">

    <ng-template #headerTemplate>
        @if(header()) {
        <div class="mb-4">
            <ng-container *ngTemplateOutlet="header()!" />
        </div>
        } @else {
        <div class="flex items-center justify-between px-4 py-3 border-b border-gray-200 flex-shrink-0">
            <div class="font-bold text-base">{{ getTitle() }}</div>
            <app-button variant="text" severity="contrast" icon="x" iconSource="lucide" iconSize="sm"
                (clicked)="handleDialogClose()" />
        </div>
        }
    </ng-template>

    <ng-template #bodyTemplate>
        <ng-container *ngTemplateOutlet="fileSelectionBodyInner" />
    </ng-template>

    <ng-template #footerTemplate>
        <ng-container *ngTemplateOutlet="fileSelectionFooter" />
    </ng-template>

</app-dialog>
}
